// src/services/invoiceService.js
import api from 'services/api';

export const invoiceService = {
  // ========================================
  // UNIFIED INVOICE METHODS (Role-based access handled by backend)
  // ========================================

  // Get invoices (filtered by role automatically)
  getInvoices: async (params = {}) => {
    const response = await api.get('/invoices', { params });
    return response.data;
  },

  // Get single invoice
  getInvoice: async (id) => {
    const response = await api.get(`/invoices/${id}`);
    return response.data;
  },

  // Get invoice statistics
  getStatistics: async () => {
    const response = await api.get('/invoices/statistics');
    return response.data;
  },

  // Get monthly financial summaries (admin only)
  getMonthlySummary: async () => {
    const response = await api.get('/invoices/monthly-summary');
    return response.data;
  },

  // Get invoices by project
  getInvoicesByProject: async (projectId, params = {}) => {
    const response = await api.get(`/invoices/by-project/${projectId}`, { params });
    return response.data;
  },

  // Get invoices by client
  getInvoicesByClient: async (clientId, params = {}) => {
    const response = await api.get(`/invoices/by-client/${clientId}`, { params });
    return response.data;
  },

  // Get invoices by vendor
  getInvoicesByVendor: async (vendorId, params = {}) => {
    const response = await api.get(`/invoices/by-vendor/${vendorId}`, { params });
    return response.data;
  },

  // ========================================
  // INVOICE CRUD OPERATIONS
  // ========================================

  // Create new invoice (vendor only)
  createInvoice: async (data) => {
    const formData = new FormData();

    // Append regular fields (excluding files and timesheets array)
    Object.keys(data).forEach((key) => {
      if (data[key] !== null && data[key] !== undefined && key !== 'invoice_file' && key !== 'timesheet_file' && key !== 'timesheets') {
        formData.append(key, data[key]);
      }
    });

    // Append invoice file
    if (data.invoice_file) {
      formData.append('invoice_file', data.invoice_file);
    }

    // Legacy timesheet file support (for backward compatibility)
    if (data.timesheet_file) {
      formData.append('timesheet_file', data.timesheet_file);
    }

    // Append timesheets array
    if (data.timesheets && data.timesheets.length > 0) {
      data.timesheets.forEach((timesheet, index) => {
        // Append timesheet data as JSON string
        formData.append(`timesheets[${index}][subcontractor_id]`, timesheet.subcontractor_id || '');
        formData.append(`timesheets[${index}][billing_units]`, timesheet.billing_units || '0');
        formData.append(`timesheets[${index}][billing_unit_type]`, timesheet.billing_unit_type || 'hours');
        formData.append(`timesheets[${index}][notes]`, timesheet.notes || '');

        // Append timesheet file if provided
        if (timesheet.timesheet_file && timesheet.timesheet_file instanceof File) {
          formData.append(`timesheet_file_${index}`, timesheet.timesheet_file);
        }
      });
    }

    const response = await api.post('/invoices', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  // Update invoice (vendor only)
  updateInvoice: async (id, data) => {
    const formData = new FormData();

    // Append regular fields
    Object.keys(data).forEach((key) => {
      if (data[key] !== null && data[key] !== undefined && key !== 'invoice_file' && key !== 'timesheet_file') {
        // Handle timesheets array specially - use indexed notation for Laravel
        if (key === 'timesheets' && Array.isArray(data[key])) {
          data[key].forEach((timesheet, index) => {
            Object.keys(timesheet).forEach((tsKey) => {
              if (timesheet[tsKey] !== null && timesheet[tsKey] !== undefined) {
                formData.append(`timesheets[${index}][${tsKey}]`, timesheet[tsKey]);
              }
            });
          });
        } else {
          formData.append(key, data[key]);
        }
      }
    });

    // Append files if provided
    if (data.invoice_file instanceof File) {
      formData.append('invoice_file', data.invoice_file);
    }
    if (data.timesheet_file instanceof File) {
      formData.append('timesheet_file', data.timesheet_file);
    }

    const response = await api.post(`/invoices/${id}?_method=PUT`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  // Delete invoice (vendor only)
  deleteInvoice: async (id) => {
    const response = await api.delete(`/invoices/${id}`);
    return response.data;
  },

  // Submit invoice for approval (vendor only)
  submitInvoice: async (id) => {
    const response = await api.post(`/invoices/${id}/submit`);
    return response.data;
  },

  // ========================================
  // APPROVAL OPERATIONS
  // ========================================

  // Approve invoice (client or admin)
  approveInvoice: async (id, comment = '') => {
    const response = await api.post(`/invoices/${id}/approve`, { comment });
    return response.data;
  },

  // Reject invoice (client or admin)
  rejectInvoice: async (id, comment) => {
    const response = await api.post(`/invoices/${id}/reject`, { comment });
    return response.data;
  },

  // Mark invoice as paid (admin only)
  markAsPaid: async (id, comment = '') => {
    const response = await api.post(`/invoices/${id}/mark-as-paid`, { comment });
    return response.data;
  },

  // ========================================
  // FILE OPERATIONS
  // ========================================

  // Download invoice file
  downloadInvoiceFile: async (id) => {
    const response = await api.get(`/invoices/${id}/download-invoice`, {
      responseType: 'blob'
    });
    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'invoice.pdf';

    console.log('contentDisposition: ', contentDisposition);

    if (contentDisposition) {
      // RFC 5987 (filename*=)
      const filenameStarMatch = contentDisposition.match(/filename\*\s*=\s*UTF-8''(.+)/i);
      if (filenameStarMatch) {
        filename = decodeURIComponent(filenameStarMatch[1]);
      } else {
        // Basic filename=
        const filenameMatch = contentDisposition.match(/filename\s*=\s*"?([^";]+)"?/i);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
    }
    return { blob: response.data, filename };
  },

  // Download timesheet file
  downloadTimesheetFile: async (id) => {
    const response = await api.get(`/invoices/${id}/download-timesheet`, {
      responseType: 'blob'
    });
    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'timesheet.pdf';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[*]?=['"]?([^'"\n;]+)['"]?/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    return { blob: response.data, filename };
  },

  // Download individual timesheet file
  downloadIndividualTimesheetFile: async (invoiceId, timesheetId) => {
    const response = await api.get(`/invoices/${invoiceId}/timesheets/${timesheetId}/download`, {
      responseType: 'blob'
    });
    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'timesheet.pdf';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[*]?=['"]?([^'"\n;]+)['"]?/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    return { blob: response.data, filename };
  },

  // ========================================
  // COMMENT OPERATIONS
  // ========================================

  // Get invoice comments
  getComments: async (id) => {
    const response = await api.get(`/invoices/${id}/comments`);
    return response.data;
  },

  // Add comment
  addComment: async (id, comment) => {
    const response = await api.post(`/invoices/${id}/comments`, { comment });
    return response.data;
  },

  // ========================================
  // CLIENT TIMESHEETS
  // ========================================

  // Get client timesheets (approved invoices with timesheets from monthly/hourly projects)
  getClientTimesheets: async (params = {}) => {
    console.log('getClientTimesheets');
    const response = await api.get('/invoices/timesheets', { params });
    return response.data;
  },

  // Add comment to timesheet
  addTimesheetComment: async (id, comment) => {
    const response = await api.post(`/invoices/${id}/timesheet-comment`, { comment });
    return response.data;
  },

  // ========================================
  // ADMIN OPERATIONS
  // ========================================

  // Update billing month (admin only)
  updateBillingMonth: async (id, billingMonth) => {
    const response = await api.put(`/invoices/${id}/billing-month`, { billing_month: billingMonth });
    return response.data;
  },

  // Update billing hours for legacy invoices (admin only)
  updateBillingHours: async (id, billingUnits, billingUnitType) => {
    const response = await api.put(`/invoices/${id}/billing-hours`, {
      billing_units: billingUnits,
      billing_unit_type: billingUnitType
    });
    return response.data;
  },

  // Update timesheet billing units (admin only)
  updateTimesheetBillingUnits: async (invoiceId, timesheetId, billingUnits, billingUnitType) => {
    const response = await api.put(`/invoices/${invoiceId}/timesheets/${timesheetId}/billing-units`, {
      billing_units: billingUnits,
      billing_unit_type: billingUnitType
    });
    return response.data;
  },

  // ========================================
  // UTILITY METHODS
  // ========================================

  // Helper: Download file with proper filename
  downloadFile: (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
};

export default invoiceService;
